/**
 * Pure itinerary validation, ported from the legacy
 * src/main/webapp/app/home/home.component.ts#validateItinerary.
 *
 * Inputs are TravelLeg objects (a thin in-memory shape that includes the
 * date split into separate date and time fields like the legacy form uses).
 * The validator returns ordered errors per leg index plus optional whole-
 * trip errors (Madhuban presence).
 *
 * Unit-test this in isolation — these rules are the only non-obvious
 * business logic in the daily-driver screen.
 */

export const MADHUBAN_LOCATION_ID = 1;
export type Transport = 'plane' | 'road' | 'train' | 'stay';

export interface TravelLeg {
  id?: number | null;
  transport: Transport | string;
  departureDate?: string | null; // YYYY-MM-DD
  departureTime?: string | null; // HH:MM
  arrivalDate?: string | null;   // YYYY-MM-DD
  arrivalTime?: string | null;   // HH:MM
  departureFromId?: number | null;
  arrivalToId?: number | null;
}

export interface LegError {
  legIndex: number;
  message: string;
}

const ONE_DAY_MS = 86_400_000;

export function joinDateTime(date: string | null | undefined, time: string | null | undefined): number {
  if (!date) return 0;
  // `date` may be a plain 'YYYY-MM-DD' OR a full 'YYYY-MM-DDTHH:mm[:ss]' (the
  // itinerary editor stores full datetimes). Normalise to the date part and
  // prefer the explicit `time`, falling back to any time embedded in `date`.
  const datePart = date.slice(0, 10);
  const embedded = date.match(/T(\d{2}:\d{2})/)?.[1];
  const t = (time && /^\d{2}:\d{2}$/.test(time)) ? time : (embedded ?? '00:00');
  return new Date(`${datePart}T${t}:00`).getTime();
}

/**
 * Sort legs in display order: by departure date, then within a same-date pair
 * by location continuity (if A.from == B.to then A goes second), falling
 * back to arrival date/time comparison.
 */
export function sortLegs<T extends TravelLeg>(legs: T[]): T[] {
  return [...legs].sort((a, b) => {
    const ad = a.departureDate ?? '';
    const bd = b.departureDate ?? '';
    if (ad === bd) {
      if (a.departureFromId != null && b.arrivalToId != null && a.departureFromId === b.arrivalToId) return 1;
      if (b.departureFromId != null && a.arrivalToId != null && b.departureFromId === a.arrivalToId) return -1;
      return joinDateTime(a.arrivalDate, a.arrivalTime) - joinDateTime(b.arrivalDate, b.arrivalTime);
    }
    return ad < bd ? -1 : 1;
  });
}

/**
 * Validates an itinerary. Caller should pass sorted legs (use sortLegs first).
 * Returns an array of {legIndex, message} — indices reference the sorted array.
 */
export function validateItinerary(sorted: readonly TravelLeg[]): LegError[] {
  const errors: LegError[] = [];
  let hasArrivedMadhuban = false;
  let hasLeftMadhuban = false;

  for (let i = 0; i < sorted.length; i++) {
    const curr = sorted[i]!;
    const prev = i > 0 ? sorted[i - 1]! : null;
    if (prev) {
      const prevArr = joinDateTime(prev.arrivalDate, prev.arrivalTime);
      const currDep = joinDateTime(curr.departureDate, curr.departureTime);

      if (
        prev.transport !== 'road' &&
        curr.transport !== 'road' &&
        curr.transport !== 'stay' &&
        prevArr > currDep
      ) {
        errors.push({
          legIndex: i,
          message: prev.transport !== 'stay'
            ? 'Departure before arrival'
            : 'Departing early, please correct accommodation dates',
        });
      } else if (
        prev.transport !== 'road' &&
        prev.transport !== 'stay' &&
        prev.arrivalToId != null &&
        curr.departureFromId != null &&
        prev.arrivalToId !== curr.departureFromId
      ) {
        errors.push({ legIndex: i, message: 'Departing from different location, possible missing leg' });
      } else if (currDep - prevArr > ONE_DAY_MS) {
        errors.push({ legIndex: i, message: 'More than a day between arrival and departure, possible missing accommodation entry' });
      }
    }
    if (curr.arrivalToId === MADHUBAN_LOCATION_ID) hasArrivedMadhuban = true;
    if (curr.departureFromId === MADHUBAN_LOCATION_ID) hasLeftMadhuban = true;
  }

  if (sorted.length > 0) {
    if (!hasArrivedMadhuban) {
      errors.unshift({ legIndex: 0, message: 'Trip does not seem to include Madhuban' });
    } else if (!hasLeftMadhuban) {
      errors.unshift({ legIndex: 0, message: 'Trip does not seem to include leaving Madhuban' });
    }
  }

  return errors;
}

/**
 * Compute defaults for a new leg appended to an existing list. Mirrors the
 * legacy `add()` method's behaviour.
 */
export function defaultsForAppendedLeg(prev: TravelLeg | null): Partial<TravelLeg> {
  const defaults: Partial<TravelLeg> = { transport: 'plane' };
  if (!prev) return defaults;
  if (prev.departureFromId != null || prev.transport === 'stay') {
    defaults.departureFromId = prev.transport === 'stay' ? prev.departureFromId : prev.arrivalToId;
  }
  if (prev.arrivalDate) {
    defaults.departureDate = prev.arrivalDate;
    defaults.arrivalDate = prev.arrivalDate;
  }
  return defaults;
}

/**
 * Apply implicit field normalisation before saving a leg. Matches the
 * legacy `saveWithRefresh` pre-processing.
 *   - road: arrivalDate = departureDate; departureTime = arrivalTime
 *   - stay: arrivalToId = departureFromId
 *   - empty times → '00:00'
 */
export function normaliseLegForSave<T extends TravelLeg>(leg: T): T {
  const out = { ...leg };
  if (out.transport === 'road') {
    // A road leg is a single-day trip with one "approx arrival" time. Both
    // ends share that date+time. Rebuild both datetimes from the date part and
    // the arrival time so the time isn't lost — the previous code copied
    // departureDate (midnight) over arrivalDate and dropped the real time.
    const datePart = (out.departureDate ?? out.arrivalDate ?? '').slice(0, 10);
    const t = out.arrivalTime || out.departureTime || '00:00';
    out.departureTime = t;
    out.arrivalTime = t;
    if (datePart) {
      out.departureDate = `${datePart}T${t}:00`;
      out.arrivalDate = `${datePart}T${t}:00`;
    }
  }
  if (out.transport === 'stay') {
    out.arrivalToId = out.departureFromId;
  }
  if (!out.departureTime) out.departureTime = '00:00';
  if (!out.arrivalTime) out.arrivalTime = '00:00';
  return out;
}

/** Build the 48 half-hour options the legacy time picker uses. */
export function halfHourOptions(): string[] {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
}

/**
 * Server (Travel DTO) sends `departureDate`/`arrivalDate` as ISO datetime
 * strings ('YYYY-MM-DDTHH:mm:ss'). The form splits these into date+time.
 */
export function splitDateTime(iso: string | null | undefined): { date: string | null; time: string | null } {
  if (!iso) return { date: null, time: null };
  // Expected: "YYYY-MM-DDTHH:mm:ss" — strip any timezone suffix the backend might add.
  const noZ = iso.replace(/Z$/, '').replace(/[+-]\d{2}:?\d{2}$/, '');
  const m = noZ.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (!m) return { date: noZ.slice(0, 10) || null, time: null };
  return { date: m[1]!, time: `${m[2]}:${m[3]}` };
}

export function mergeDateTime(date: string | null | undefined, time: string | null | undefined): string | null {
  if (!date) return null;
  // Accept a date-only or full-ISO `date`; keep the date part and prefer the
  // explicit `time`, else any time already embedded in `date`.
  const datePart = date.slice(0, 10);
  const embedded = date.match(/T(\d{2}:\d{2})/)?.[1];
  const t = (time && /^\d{2}:\d{2}$/.test(time)) ? time : (embedded ?? '00:00');
  return `${datePart}T${t}:00`;
}
